import { DestroyRef, inject, Injectable } from '@angular/core';
import { NavigationEnd, Route, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MAIN_ROUTES } from '@routes';
import { CONTEXT_KEY, JSONValue } from '@models';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  #context: Map<CONTEXT_KEY, JSONValue> = new Map<CONTEXT_KEY, JSONValue>();
  #currentLocation: BehaviorSubject<string> = new BehaviorSubject<string>('');
  #destroyRef: DestroyRef = inject(DestroyRef);
  public currentLocation$: Observable<string> =
    this.#currentLocation.asObservable();

  constructor(private router: Router) {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((event: NavigationEnd): void => {
        this.#currentLocation.next(event.urlAfterRedirects);
      });
  }

  public get isInMainRoutes(): boolean {
    const currentPath: string =
      this.#currentLocation.value.split('/').pop() || '';

    const hasNestedRoute = (routes: Route[]): boolean => {
      return routes.some((route: Route): boolean => {
        if (route.path === currentPath) {
          return true;
        }
        return route.children ? hasNestedRoute(route.children) : false;
      });
    };

    return hasNestedRoute(MAIN_ROUTES);
  }

  public setContext(key: CONTEXT_KEY, value: JSONValue): void {
    this.#context.set(key, value);
  }

  public getContext(key: CONTEXT_KEY): JSONValue {
    return this.#context.get(key) as JSONValue;
  }

  public clearContext(): void {
    this.#context.clear();
  }

  public clearContextByKey(key: CONTEXT_KEY): void {
    this.#context.delete(key);
  }

  public getUserContext(): JSONValue {
    return this.getContext(CONTEXT_KEY.USER);
  }

  public getApplicationContext(): JSONValue {
    return this.getContext(CONTEXT_KEY.SETTINGS);
  }
}
